import { useState } from 'react'
import { Label } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Plus, X, Settings } from 'lucide-react'
import type { CommunityInput, SavedWorldBuilding, SavedCommunity, SavedCharacter, SavedEnemy } from '@creanizer/types'
import type { CustomValue } from '@creanizer/utils'
import { WorldContextForm } from './WorldContextForm'
import { CommunityContextForm } from './CommunityContextForm'
import { CharacterContextForm } from './CharacterContextForm'
import { EnemyContextForm } from './EnemyContextForm'

interface StandardModeFormProps {
  input: CommunityInput
  onChange: (input: CommunityInput) => void
  savedCommunityTypes?: CustomValue[]
  savedWorlds?: SavedWorldBuilding[]
  savedCommunities?: SavedCommunity[]
  characters?: SavedCharacter[]
  enemies?: SavedEnemy[]
  addCustomValue?: (type: CustomValue['type'], value: string, label: string, generatorType?: CustomValue['generatorType']) => Promise<void>
  useCustomValue?: (id: string) => Promise<void>
}

export function StandardModeForm({ 
  input, 
  onChange, 
  savedCommunityTypes = [],
  savedWorlds = [],
  savedCommunities = [],
  characters = [],
  enemies = [],
  addCustomValue,
  useCustomValue 
}: StandardModeFormProps) {
  const [featureInput, setFeatureInput] = useState('')
  const [problemInput, setProblemInput] = useState('')
  const [secretInput, setSecretInput] = useState('')

  const handleAddFeature = () => {
    if (featureInput.trim()) {
      onChange({
        ...input,
        uniqueFeatures: [...(input.uniqueFeatures || []), featureInput.trim()]
      })
      setFeatureInput('')
    }
  }

  const handleAddProblem = () => {
    if (problemInput.trim()) {
      onChange({
        ...input,
        problems: [...(input.problems || []), problemInput.trim()]
      })
      setProblemInput('')
    }
  }

  const handleAddSecret = () => {
    if (secretInput.trim()) {
      onChange({
        ...input,
        secrets: [...(input.secrets || []), secretInput.trim()]
      })
      setSecretInput('')
    }
  }

  return (
    <div className="space-y-6">
      {/* 世界観連携 */}
      <WorldContextForm
        input={input}
        onChange={onChange}
        savedWorlds={savedWorlds}
      />
      
      {/* 関連コミュニティ連携 */}
      <CommunityContextForm
        input={input}
        onChange={onChange}
        savedCommunities={savedCommunities}
      />
      
      {/* 関連キャラクター連携 */}
      <CharacterContextForm
        input={input}
        onChange={onChange}
        characters={characters}
      />
      
      {/* 関連エネミー連携 */}
      <EnemyContextForm
        input={input}
        onChange={onChange}
        enemies={enemies}
      />
      
      {/* 基本設定 */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold">基本設定</h3>
        
        <div className="space-y-2">
          <Label htmlFor="name">共同体名（省略可）</Label>
          <Input
            id="name"
            placeholder="例：王都エルドラス、港町ミストハーバー、山岳要塞グリムホルド"
            value={input.name || ''}
            onChange={(e) => onChange({ ...input, name: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="communityType">共同体タイプ</Label>
          <Select
            value={input.customCommunityType ? 'custom' : input.communityType}
            onValueChange={(value: any) => {
              if (value === 'custom') {
                onChange({ ...input, communityType: 'custom', customCommunityType: '' })
              } else {
                onChange({ ...input, communityType: value, customCommunityType: undefined })
              }
            }}
          >
            <SelectTrigger id="communityType">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="nation">国</SelectItem>
              <SelectItem value="state">州</SelectItem>
              <SelectItem value="territory">領地</SelectItem>
              <SelectItem value="metropolis">大都市</SelectItem>
              <SelectItem value="city">都市</SelectItem>
              <SelectItem value="town">町</SelectItem>
              <SelectItem value="village">村</SelectItem>
              <SelectItem value="hamlet">集落</SelectItem>
              <SelectItem value="island">島</SelectItem>
              <SelectItem value="custom">カスタム...</SelectItem>
            </SelectContent>
          </Select>
          
          {/* カスタムタイプ入力 */}
          {(input.communityType === 'custom' || input.customCommunityType) && (
            <div className="space-y-2">
              <Input
                placeholder="カスタム共同体タイプを入力（例: 要塞都市、港町、学術都市など）"
                value={input.customCommunityType || ''}
                onChange={(e) => onChange({ ...input, customCommunityType: e.target.value })}
              />
              {input.customCommunityType && addCustomValue && (
                <Button
                  type="button"
                  onClick={async () => {
                    await addCustomValue(
                      'communityType' as any,
                      input.customCommunityType!,
                      input.customCommunityType!,
                      'community'
                    )
                    alert(`カスタム共同体タイプ "${input.customCommunityType}" を保存しました`)
                  }}
                  variant="secondary"
                  size="sm"
                >
                  💾 保存
                </Button>
              )}
            </div>
          )}
          
          {/* 保存済みカスタムタイプ */}
          {savedCommunityTypes.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-gray-600">保存済みカスタム:</Label>
              <div className="flex flex-wrap gap-2">
                {savedCommunityTypes.map((saved) => (
                  <Button
                    key={saved.id}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      onChange({ ...input, communityType: 'custom', customCommunityType: saved.value })
                      if (useCustomValue) {
                        await useCustomValue(saved.id)
                      }
                    }}
                  >
                    {saved.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="scale">規模</Label>
          <Select
            value={input.scale}
            onValueChange={(value: any) => onChange({ ...input, scale: value })}
          >
            <SelectTrigger id="scale">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="tiny">極小</SelectItem>
              <SelectItem value="small">小</SelectItem>
              <SelectItem value="medium">中</SelectItem>
              <SelectItem value="large">大</SelectItem>
              <SelectItem value="huge">巨大</SelectItem>
              <SelectItem value="massive">超巨大</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="population">人口（省略可）</Label>
          <Input
            id="population"
            type="number"
            value={input.population || ''}
            onChange={(e) => onChange({ ...input, population: parseInt(e.target.value) || undefined })}
            placeholder="自動計算されます"
          />
        </div>
      </div>

      {/* 経済設定 */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold">経済設定</h3>
        
        <div className="space-y-2">
          <Label htmlFor="wealthLevel">豊かさレベル</Label>
          <Select
            value={input.wealthLevel || ''}
            onValueChange={(value: any) => onChange({ ...input, wealthLevel: value || undefined })}
          >
            <SelectTrigger id="wealthLevel">
              <SelectValue placeholder="選択してください" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="impoverished">困窮</SelectItem>
              <SelectItem value="poor">貧困</SelectItem>
              <SelectItem value="modest">質素</SelectItem>
              <SelectItem value="comfortable">快適</SelectItem>
              <SelectItem value="prosperous">繁栄</SelectItem>
              <SelectItem value="wealthy">裕福</SelectItem>
              <SelectItem value="opulent">豪華</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* 特殊要素 */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold">特殊要素</h3>
        
        {/* ユニークな特徴 */}
        <div className="space-y-2">
          <Label>ユニークな特徴</Label>
          <div className="flex space-x-2">
            <Input
              value={featureInput}
              onChange={(e) => setFeatureInput(e.target.value)}
              placeholder="例：巨大な時計塔がシンボル、常に霧に包まれている"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddFeature()
                }
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddFeature}
              disabled={!featureInput.trim()}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {input.uniqueFeatures?.map((feature, index) => (
              <Badge key={index} variant="secondary">
                {feature}
                <button
                  onClick={() => onChange({
                    ...input,
                    uniqueFeatures: input.uniqueFeatures?.filter((_, i) => i !== index)
                  })}
                  className="ml-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>

        {/* 現在の問題 */}
        <div className="space-y-2">
          <Label>現在の問題</Label>
          <div className="flex space-x-2">
            <Input
              value={problemInput}
              onChange={(e) => setProblemInput(e.target.value)}
              placeholder="例：水不足、盗賊団の脅威、伝染病の流行"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddProblem()
                }
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddProblem}
              disabled={!problemInput.trim()}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {input.problems?.map((problem, index) => (
              <Badge key={index} variant="destructive">
                {problem}
                <button
                  onClick={() => onChange({
                    ...input,
                    problems: input.problems?.filter((_, i) => i !== index)
                  })}
                  className="ml-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>

        {/* 隠された秘密 */}
        <div className="space-y-2">
          <Label>隠された秘密</Label>
          <div className="flex space-x-2">
            <Input
              value={secretInput}
              onChange={(e) => setSecretInput(e.target.value)}
              placeholder="例：地下に古代遺跡、支配者は実は悪魔と契約"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddSecret()
                }
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddSecret}
              disabled={!secretInput.trim()}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {input.secrets?.map((secret, index) => (
              <Badge key={index} variant="outline">
                {secret}
                <button
                  onClick={() => onChange({
                    ...input,
                    secrets: input.secrets?.filter((_, i) => i !== index)
                  })}
                  className="ml-1"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        </div>
      </div>
      
      {/* 追加設定 */}
      <div className="space-y-4 border-t pt-4">
        <div className="flex items-center space-x-2">
          <Settings className="h-4 w-4" />
          <h3 className="text-sm font-semibold">追加設定（オプション）</h3>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="additionalRequirements">追加の要件や指示</Label>
          <Textarea
            id="additionalRequirements"
            value={input.additionalRequirements || ''}
            onChange={(e) => onChange({ ...input, additionalRequirements: e.target.value })}
            placeholder="例：&#10;・山間部の隠れ里として設定&#10;・魔法使いが多く住む学術都市&#10;・交易路の要衝として繁栄&#10;・古代遺跡の上に建設された都市&#10;・異種族が共存する多文化都市&#10;・地下に巨大な迷宮が存在&#10;・特定の産業（鍛冶、薬草、魔法道具など）で有名"
            rows={5}
          />
          <p className="text-xs text-muted-foreground">
            共同体に含めたい要素や詳細な指示を自由に記述できます。AIが指示を理解して生成に反映します。
          </p>
        </div>
      </div>
    </div>
  )
}